import React, { useContext, useEffect } from 'react';
import { Box, Stack, Paper } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AccordionSummary, AccordionDetails, Accordion } from '../shared/components/accordion';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import ConfirmDialog from '../shared/components/confirmDialog';
import { FormMode } from '../utils/extensions/constantEx'
import EditIcon from '@mui/icons-material/Edit';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import { NurseSalaryPaySearchAPIUrl, GetNoToCurrencyFormat, GetDayJSValueFromUTCDate, NurseSalaryPaymentAPIUrl } from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import { useCookies } from 'react-cookie';

const NurseSalaryPaymentList = (props) => {
    const { expanded, formOpenMode, nurseId, handleAccordionChange, handleEditSalaryPaymentClick, handlePageLoadingOpen, handlePageLoadingClose } = props;
    const { ShowAPIError, OpenErrorAlert, homeState, OpenSuccessAlert, ClearLoggedUser } = useContext(HomeContext);
    const { salaryPayments, SetSalaryPayments } = useContext(NurseFormContext);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(0);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (nurseId && nurseId > 0) {
            fetchNurseSalaryPayments();
        }
    }, [nurseId]);

    async function fetchNurseSalaryPayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseSalaryPaySearchAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetSalaryPayments(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setIsDeleteConfirmOpen(true);
    }

    const handleDeleteOKClick = async () => {
        try {
            handlePageLoadingOpen();
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseSalaryPaymentAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify({ 'id': selectedId })
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Nurse salary payment successfully deleted.');
                await fetchNurseSalaryPayments();
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setIsDeleteConfirmOpen(false);
            setSelectedId(0);
            handlePageLoadingClose();
        }
    }

    const handleDeleteCancelClick = () => {
        setSelectedId(0);
        setIsDeleteConfirmOpen(false);
    }

    const handleEditClick = async (id) => {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseSalaryPaymentAPIUrl(homeState.loggedUser.companyCode, id), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempPayment = await response.json();
                handleEditSalaryPaymentClick(tempPayment);
            }
            else {
                ShowAPIError(response)
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        }
    }

    return (

        <Accordion expanded={expanded === 'panelNurseSalaryPayments'} onChange={handleAccordionChange('panelNurseSalaryPayments')}>
            <AccordionSummary id="panel-nurse-salary-payments">
                <Typography>Salary Payments</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Box sx={{ pr: 1, float: 'right' }}>
                                <Stack direction="row" spacing={2}>
                                    {
                                        isDeleteConfirmOpen &&
                                        <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Delete"
                                            message="Are you sure you want to delete the salary payment?"
                                            handleClickOK={handleDeleteOKClick}
                                            handleClickCancel={handleDeleteCancelClick} />
                                    }
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                <Table stickyHeader sx={{ minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            {/* <StyledTableCell sx={{ width: '250px' }}>Payment Type</StyledTableCell> */}
                                            <StyledTableCell sx={{ width: '250px' }}>Receipt No</StyledTableCell>
                                            <StyledTableCell sx={{ width: '300px' }}>Pay Date</StyledTableCell>
                                            <StyledTableCell sx={{ width: '350px' }}>Amount</StyledTableCell>
                                            <StyledTableCell>Notes</StyledTableCell>
                                            <StyledTableCell sx={{ width: '100px' }}>&nbsp;</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {salaryPayments.map((row) => (
                                            <StyledTableRow key={row.id.toString()}>
                                                {/* <StyledTableCell>{row.pay_type}</StyledTableCell> */}
                                                <StyledTableCell component="th" scope="row">
                                                    {row.receipt_no.toString()}
                                                </StyledTableCell>
                                                <StyledTableCell>{GetDayJSValueFromUTCDate(row.pay_date).format(DATE_DISPLAY_FORMAT)}</StyledTableCell>
                                                <StyledTableCell>{GetNoToCurrencyFormat(row.amount)}</StyledTableCell>
                                                <StyledTableCell>{row.remarks}</StyledTableCell>
                                                <StyledTableCell>

                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        sx={{
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {
                                                            formOpenMode === FormMode.ViewMode &&
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <IconButton size="small" onClick={() => handleEditClick(row.id)}>
                                                                    <EditIcon fontSize="inherit" />
                                                                </IconButton>
                                                            </Stack>
                                                        }
                                                        {
                                                            formOpenMode === FormMode.ViewMode &&
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.id)}>
                                                                    <RemoveCircleOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        }</Stack>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Stack>
            </AccordionDetails>
        </Accordion>

    );
}
export default NurseSalaryPaymentList;